// ClaimFiles.js — CLAIM-FILES-1 Phase 4 (2026-06-24). A macOS-Finder-style 3-pane
// file manager for a claim: folder tree (nested, expand/collapse, drag/drop) · file
// list · preview pane. One home per file. Global default folders (shown on every claim)
// + claim-local folders/subfolders; core-workflow folders (Claim Bills/Payments/Fee
// Invoices) are read-only. Backend: folders_api.py + /claims/{id}/documents(+folder_id).
import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch, fmt } from "../api";

const fsize = (b) => b == null ? "—" : b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB";
// Short type label from the filename extension, falling back to the mime subtype.
const ftype = (d) => {
  const n = d.original_filename || "";
  const i = n.lastIndexOf(".");
  if (i > 0 && n.length - i <= 6) return n.slice(i + 1).toUpperCase();
  const m = (d.mime_type || "").split("/")[1] || "";
  return (m || "file").toUpperCase().slice(0, 8);
};
const typePill = { fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, padding: "1px 6px", borderRadius: 8, background: "var(--bg3, #e8eaf2)", color: "var(--text2)", whiteSpace: "nowrap" };

// H16: `seen` bounds the walk to one visit per folder per path. A parent cycle in
// the data (which the backend now refuses to create, and whose tree read is now
// depth-capped) would otherwise recurse here forever and hang the Files tab even
// after the server answered.
function buildTree(folders) {
  const byParent = {};
  folders.forEach(f => { const p = f.parent_id || 0; (byParent[p] = byParent[p] || []).push(f); });
  const attach = (pid, seen) => (byParent[pid] || [])
    .filter(f => !seen.has(f.id))
    .map(f => ({ ...f, children: attach(f.id, new Set(seen).add(f.id)) }));
  return attach(0, new Set());
}

// Preview pane — blob from the canonical download endpoint, rendered by mime.
function FilePreview({ doc }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setUrl(null); setErr(false);
    if (!doc) return;
    let dead = false, obj = null;
    apiFetch(`/documents/${doc.id}/download`, { raw: true })
      .then(r => { if (!r.ok) throw new Error("x"); return r.blob(); })
      .then(b => { if (dead) return; obj = URL.createObjectURL(b); setUrl(obj); })
      .catch(() => { if (!dead) setErr(true); });
    return () => { dead = true; if (obj) URL.revokeObjectURL(obj); };
  }, [doc?.id]);
  const wrap = { flex: 1, minHeight: 0, border: "1px solid var(--border)", borderRadius: 8, background: "#f8fafc", display: "flex", flexDirection: "column", overflow: "hidden" };
  if (!doc) return <div style={{ ...wrap, alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 13 }}>Select a file to preview</div>;
  const mime = doc.mime_type || "";
  return (
    <div style={wrap}>
      <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", fontSize: 12, fontWeight: 600, color: "var(--navy)", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.original_filename}</span>
        {doc.is_phi && <span className="badge" style={{ background: "#fee2e2", color: "#991b1b", fontSize: 9.5 }}>PHI</span>}
        {url && <a href={url} download={doc.original_filename} target="_blank" rel="noreferrer" style={{ color: "var(--blue)", fontSize: 11 }}>open ↗</a>}
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" }}>
        {err ? <span style={{ color: "var(--text3)", fontSize: 13 }}>Couldn't load preview.</span>
          : !url ? <span style={{ color: "var(--text3)", fontSize: 13 }}>Loading…</span>
          : mime.startsWith("image/") ? <img src={url} alt={doc.original_filename} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          : mime === "application/pdf" ? <iframe title="preview" src={url} style={{ width: "100%", height: "100%", border: "none" }} />
          : <a href={url} download={doc.original_filename} style={{ color: "var(--blue)", fontSize: 13 }}>Download {doc.original_filename}</a>}
      </div>
    </div>
  );
}

export default function ClaimFiles({ claimId, canEdit }) {
  const [folders, setFolders] = useState([]);
  const [sel, setSel] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [files, setFiles] = useState([]);
  const [selFile, setSelFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [extOver, setExtOver] = useState(false);   // external files dragged over the intake zone
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editFileId, setEditFileId] = useState(null);   // file (document) rename
  const [editFileValue, setEditFileValue] = useState("");
  const dragDoc = useRef(null);
  const dragFolder = useRef(null);
  const fileInput = useRef(null);

  const loadFolders = useCallback(() => apiFetch(`/claims/${claimId}/folders`).then(d => setFolders(d || [])).catch(() => setFolders([])), [claimId]);
  const loadFiles = useCallback((fid) => { if (!fid) { setFiles([]); return; } apiFetch(`/claims/${claimId}/folders/${fid}/documents`).then(d => setFiles(d || [])).catch(() => setFiles([])); }, [claimId]);
  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadFiles(sel); setSelFile(null); }, [sel, loadFiles]);
  const refresh = () => { loadFolders(); loadFiles(sel); };

  const tree = buildTree(folders);
  const selFolder = folders.find(f => f.id === sel);
  const selDoc = files.find(d => d.id === selFile);

  const act = async (fn) => { if (busy) return; setBusy(true); setMsg(null); try { await fn(); } catch (e) { setMsg(String(e.message || e).slice(0, 220)); } finally { setBusy(false); } };
  const moveDoc = (docId, folderId) => act(async () => { await apiFetch(`/documents/${docId}/folder`, { method: "PATCH", body: { folder_id: folderId } }); refresh(); });
  const moveFolder = (folderId, parentId) => act(async () => { await apiFetch(`/folders/${folderId}`, { method: "PATCH", body: { parent_id: parentId } }); refresh(); });
  // H16: mirror the backend's 409 client-side — dropping a folder onto its own
  // descendant is a cycle. Walks parent_id upward from the candidate target;
  // hop-capped so a bad row can't spin the browser either. The drop is refused
  // AND the target never highlights, so the gesture is structurally unavailable
  // rather than merely error-reported.
  const isSelfOrDescendant = (targetId, rootId) => {
    if (!targetId || !rootId) return false;
    const byId = {};
    folders.forEach(f => { byId[f.id] = f; });
    let cur = byId[targetId], hops = 0;
    while (cur && hops++ < 64) {
      if (cur.id === rootId) return true;
      cur = cur.parent_id ? byId[cur.parent_id] : null;
    }
    return false;
  };
  const newFolder = () => act(async () => {
    // Finder-style: drop an "untitled folder" and immediately put it in rename mode.
    const r = await apiFetch(`/claims/${claimId}/folders`, { method: "POST", body: { name: "untitled folder", parent_id: sel || null } });
    if (sel) setExpanded(s => new Set(s).add(sel));
    await loadFolders();
    if (r && r.id) { setEditingId(r.id); setEditValue("untitled folder"); }
  });
  const saveRename = (f) => { const v = editValue.trim(); setEditingId(null); if (!v || v === f.name) return; act(async () => { await apiFetch(`/folders/${f.id}`, { method: "PATCH", body: { name: v } }); refresh(); }); };
  const delFolder = (f) => { if (!window.confirm(`Delete folder “${f.name}”? It must be empty.`)) return; act(async () => { await apiFetch(`/folders/${f.id}`, { method: "DELETE" }); if (sel === f.id) setSel(null); refresh(); }); };
  // Upload one or many files into the selected folder (intake surface). PHI default
  // + folder are inherited from the selected folder; the list refreshes when done.
  const uploadMany = (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (!arr.length) return;
    act(async () => {
      for (const file of arr) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("is_phi", String(!!(selFolder && selFolder.is_phi_default)));
        if (sel) fd.append("folder_id", String(sel));
        await apiFetch(`/claims/${claimId}/documents`, { method: "POST", raw: true, body: fd });
      }
      refresh();
    });
  };
  const delFile = (d) => { if (!window.confirm(`Delete “${d.original_filename}”?`)) return; act(async () => { await apiFetch(`/documents/${d.id}`, { method: "DELETE" }); refresh(); }); };
  const saveFileRename = (d) => { const v = editFileValue.trim(); setEditFileId(null); if (!v || v === d.original_filename) return; act(async () => { await apiFetch(`/documents/${d.id}`, { method: "PATCH", body: { original_filename: v } }); refresh(); }); };

  const renderFolder = (f, depth) => {
    const isOpen = expanded.has(f.id);
    const isSel = sel === f.id;
    const isDrop = dropTarget === f.id;
    const canDrop = canEdit && !f.is_core_workflow;
    return (
      <div key={f.id}>
        <div
          onClick={() => setSel(f.id)}
          draggable={canEdit && !f.is_global && !f.is_core_workflow}
          onDragStart={e => { dragFolder.current = f.id; dragDoc.current = null; e.stopPropagation(); }}
          onDragOver={e => { if (!canDrop) return; if (dragFolder.current && isSelfOrDescendant(f.id, dragFolder.current)) return; e.preventDefault(); setDropTarget(f.id); }}
          onDragLeave={() => setDropTarget(t => (t === f.id ? null : t))}
          onDrop={e => {
            e.preventDefault(); setDropTarget(null);
            if (!canDrop) return;
            if (dragDoc.current) moveDoc(dragDoc.current, f.id);
            else if (dragFolder.current && dragFolder.current !== f.id) {
              if (isSelfOrDescendant(f.id, dragFolder.current)) setMsg("Can't move a folder into one of its own subfolders.");
              else moveFolder(dragFolder.current, f.id);
            }
            dragDoc.current = null; dragFolder.current = null;
          }}
          style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 6px", paddingLeft: 6 + depth * 14, cursor: "pointer", fontSize: 13, borderRadius: 5, marginBottom: 1, background: isDrop ? "#dbeafe" : isSel ? "var(--navy)" : "transparent", color: isSel ? "#fff" : "var(--navy)" }}>
          <span onClick={e => { e.stopPropagation(); setExpanded(s => { const n = new Set(s); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; }); }}
            style={{ width: 12, textAlign: "center", color: isSel ? "#fff" : "var(--text3)", visibility: f.children.length ? "visible" : "hidden", cursor: "pointer", fontSize: 10 }}>{isOpen ? "▾" : "▸"}</span>
          <span style={{ fontSize: 13 }}>{f.is_core_workflow ? "🔒" : "📁"}</span>
          {editingId === f.id
            ? <input autoFocus value={editValue} onClick={e => e.stopPropagation()} onFocus={e => e.target.select()}
                onChange={e => setEditValue(e.target.value)} onBlur={() => saveRename(f)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); saveRename(f); } else if (e.key === "Escape") { setEditingId(null); } }}
                style={{ flex: 1, minWidth: 0, fontSize: 13, padding: "0 3px", border: "1px solid var(--blue)", borderRadius: 3 }} />
            : <span onDoubleClick={e => { if (canEdit && !f.is_global && !f.is_core_workflow) { e.stopPropagation(); setEditingId(f.id); setEditValue(f.name); } }}
                title={canEdit && !f.is_global && !f.is_core_workflow ? "Double-click to rename" : undefined}
                style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>}
          {(f.rollup_count ?? f.doc_count) > 0 && <span style={{ fontSize: 10.5, opacity: 0.7 }} title={f.children && f.children.length ? "Total including subfolders" : undefined}>{f.rollup_count ?? f.doc_count}</span>}
        </div>
        {isOpen && f.children.map(c => renderFolder(c, depth + 1))}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", gap: 0, height: 580, border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "#fff" }}>
      <div style={{ width: 244, flexShrink: 0, borderRight: "1px solid var(--border)", background: "var(--bg2, #f6f6f4)", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4, flex: 1 }}>Folders</span>
          {canEdit && <button className="btn btn-sm btn-outline" disabled={busy} onClick={newFolder} title="New folder in the selected folder (or top level)">+ Folder</button>}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 6 }}>{tree.map(f => renderFolder(f, 0))}</div>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selFolder ? selFolder.name : "Select a folder"}
            {selFolder?.is_core_workflow && <span style={{ marginLeft: 8, fontSize: 11, color: "#92400e", fontWeight: 500 }}>🔒 workflow · read-only</span>}
          </span>
          {canEdit && selFolder && !selFolder.is_core_workflow && <>
            <input ref={fileInput} type="file" multiple style={{ display: "none" }} onChange={e => { const fs = e.target.files; e.target.value = ""; uploadMany(fs); }} />
            <button className="btn btn-sm btn-blue" disabled={busy} onClick={() => fileInput.current?.click()}>{busy ? "Uploading…" : "+ Upload"}</button>
          </>}
          {canEdit && selFolder && !selFolder.is_global && !selFolder.is_core_workflow && selFolder.doc_count === 0 && !selFolder.has_children &&
            <button className="btn btn-sm btn-outline" disabled={busy} onClick={() => delFolder(selFolder)} title="Delete this empty folder">Delete</button>}
        </div>
        {msg && <div style={{ padding: "6px 10px", fontSize: 11.5, color: "#991b1b", background: "#fef2f2" }}>{msg}</div>}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {!selFolder ? <div style={{ padding: 36, color: "var(--text3)", fontSize: 13, textAlign: "center" }}>Pick a folder on the left.</div>
            : files.length === 0 ? <div style={{ padding: 36, color: "var(--text3)", fontSize: 13, textAlign: "center" }}>This folder is empty.{canEdit && !selFolder.is_core_workflow ? " Upload, or drag a file onto it." : ""}</div>
            : (
              // LD-DESIGN-012 pattern 3 — typed metadata rows: Name · Type · Date · Size · Added By.
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ width: 64 }}>Type</th>
                    <th style={{ width: 92 }}>Date</th>
                    <th style={{ width: 74 }}>Size</th>
                    <th style={{ width: 130 }}>Added By</th>
                    {canEdit && <th style={{ width: 56 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {files.map(d => {
                    const ro = d.read_only;
                    const isSelF = selFile === d.id;
                    return (
                      <tr key={d.id} onClick={() => setSelFile(d.id)}
                        draggable={(canEdit && !ro) || !!d.dl_token}
                        title="Drag to a folder to move · drag to your desktop to save a copy"
                        onDragStart={e => {
                          dragFolder.current = null;
                          dragDoc.current = (canEdit && !ro) ? d.id : null;  // internal move only when movable
                          if (d.dl_token) {  // drag-to-desktop: OS fetches this short-lived signed link
                            try { e.dataTransfer.setData("DownloadURL", `${d.mime_type || "application/octet-stream"}:${d.original_filename}:${window.location.origin}/api/documents/${d.id}/download-signed?t=${encodeURIComponent(d.dl_token)}`); } catch (_) {}
                          }
                        }}
                        style={{ cursor: "pointer", background: isSelF ? "#eff6ff" : "transparent" }}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                            <span style={{ fontSize: 14, flexShrink: 0 }}>📄</span>
                            {editFileId === d.id
                              ? <input autoFocus value={editFileValue} onClick={e => e.stopPropagation()} onFocus={e => e.target.select()}
                                  onChange={e => setEditFileValue(e.target.value)} onBlur={() => saveFileRename(d)}
                                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); saveFileRename(d); } else if (e.key === "Escape") { setEditFileId(null); } }}
                                  style={{ flex: 1, minWidth: 0, fontSize: 13, padding: "0 3px", border: "1px solid var(--blue)", borderRadius: 3 }} />
                              : <span onDoubleClick={e => { if (canEdit && !ro) { e.stopPropagation(); setEditFileId(d.id); setEditFileValue(d.original_filename); } }}
                                  title={canEdit && !ro ? "Double-click to rename" : undefined}
                                  style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--navy)" }}>{d.original_filename}</span>}
                            {d.is_phi && <span className="badge" style={{ background: "#fee2e2", color: "#991b1b", fontSize: 9.5, flexShrink: 0 }}>PHI</span>}
                            {ro && <span title="Part of a core workflow — manage it in Bills / Payments / Invoices" style={{ fontSize: 11, color: "#92400e", flexShrink: 0 }}>🔒</span>}
                          </div>
                        </td>
                        <td><span style={typePill}>{ftype(d)}</span></td>
                        <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }}>{d.created_at ? fmt.date(d.created_at) : "—"}</td>
                        <td style={{ whiteSpace: "nowrap", color: "var(--text2)" }}>{fsize(d.bytes_stored)}</td>
                        <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text2)" }}>{d.uploaded_by_name || "—"}</td>
                        {canEdit && (
                          <td style={{ whiteSpace: "nowrap" }}>
                            {!ro && <button onClick={e => { e.stopPropagation(); setEditFileId(d.id); setEditFileValue(d.original_filename); }} title="Rename" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 12, padding: "0 2px" }}>✏️</button>}
                            {!ro && <button onClick={e => { e.stopPropagation(); delFile(d); }} title="Delete" style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 12, padding: "0 2px" }}>🗑</button>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
        {/* Intake drop target — LD-DESIGN-012 pattern 4: slim dashed bar under the list. */}
        {canEdit && selFolder && !selFolder.is_core_workflow && (
          <div
            onDragOver={e => { if (Array.from(e.dataTransfer.types || []).includes("Files")) { e.preventDefault(); setExtOver(true); } }}
            onDragLeave={e => { if (e.currentTarget === e.target) setExtOver(false); }}
            onDrop={e => { if (Array.from(e.dataTransfer.types || []).includes("Files")) { e.preventDefault(); setExtOver(false); uploadMany(e.dataTransfer.files); } }}
            onClick={() => fileInput.current?.click()}
            style={{ margin: "8px 10px", border: `1.5px dashed ${extOver ? "var(--blue)" : "var(--border)"}`, borderRadius: 6, background: extOver ? "#eff6ff" : "#fafbfe", padding: "6px 12px", cursor: "pointer", color: "var(--text3)", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, whiteSpace: "nowrap", overflow: "hidden" }}>
            <span style={{ fontSize: 14, color: extOver ? "var(--blue)" : "var(--text3)" }}>⤓</span>
            {busy ? "Uploading…" : <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>Drag &amp; drop files here or <span style={{ color: "var(--blue)", fontWeight: 600 }}>Choose a file</span> — they land in <b style={{ color: "var(--navy)" }}>{selFolder.name}</b></span>}
          </div>
        )}
      </div>

      <div style={{ width: 372, flexShrink: 0, borderLeft: "1px solid var(--border)", padding: 10, display: "flex", flexDirection: "column", background: "var(--bg2, #f6f6f4)" }}>
        <FilePreview doc={selDoc} />
      </div>
    </div>
  );
}
